import { UserIcon } from '@heroicons/react/outline';
import { joiResolver } from '@hookform/resolvers/joi';
import BankIcon from 'assets/svg/icons/bankIcon';
import NewBankIcon from 'assets/svg/icons/newBankIcon';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import Loader from 'components/atoms/Loader/Loader.component';
import type { ModalApi } from 'components/atoms/Modal';
import { Modal } from 'components/atoms/Modal';
import SimpleAlertV2 from 'components/atoms/SimpleAlertV2.component';
import SimpleError from 'components/molecules/SimpleError.component';
import SearchBar from 'components/organisms/Filters/SearchBar';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCreateBankConnection from 'hooks/Bank/useCreateBankConnection';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import { AnimatePresence, motion } from 'motion/react';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Bank, BankProviders } from 'types/banks.types';
import { BankFieldType, BankStatus } from 'types/banks.types';
import { CurrenciesEnum, type QP } from 'types/global.types';
import type { Connector } from 'types/transactions.types';
import { getTracingEvents } from 'utils/amplitude';
import {
  WEBAPP_BANKSLIST_IBANITYMODAL_CLICKEDIBANINPUT,
  WEBAPP_BANKSLIST_IBANITYMODAL_CLICKEDSELECTYOURBANKSTEP,
  WEBAPP_BANKSLIST_IBANITYMODAL_CLICKEDSUBMIT,
  WEBAPP_BANKSLIST_IBANITYMODAL_CLICKEDUSERNAMEINPUT,
  WEBAPP_BANKSLIST_IBANITYMODAL_SELECTEDBANK,
} from 'utils/amplitude/events/bank.amplitude';
import { removeSoftHyphens } from 'utils/helpers';
import { ArrowLeftIconOutline, UserIconOutline } from 'utils/icons';
import useBanksList from '../../useBanksList';
import {
  checkMatchingConnector,
  getFormData,
  isUserWithLessProminentProfession,
} from './helpers';
import useVividBlockedModal from './useVividBlockedModal';
import useCanAccessAccountableBanking from 'hooks/Bank/useCanAccessAccountableBanking';
import { isEmpty } from 'lodash';
import useHasAccountableBanking from 'hooks/Bank/useHasAccountableBanking';
import accountableBankingLogo from 'assets/svg/icons/accoutableConnect/A_aldo.svg';

const tracingEvents = getTracingEvents({
  onSelectBank: {
    name: WEBAPP_BANKSLIST_IBANITYMODAL_SELECTEDBANK,
    data: (bank: string) => ({ bank }),
  },
  onClickSelectBankStep:
    WEBAPP_BANKSLIST_IBANITYMODAL_CLICKEDSELECTYOURBANKSTEP,
});

type IbanityBankSelectorProps = {
  bank?: Bank;
  successQP?: { numberOfConnectors: number } & QP;
  renderTrigger?: (props: ModalApi) => ReactNode;
  onOpenSignup: () => void;
};

type BankFormMainFieldName = 'accountReference' | 'email' | 'iban';

type BankFormFields = {
  bankId: string | number;
  provider: BankProviders;
  accountCurrency: CurrenciesEnum;
} & {
  [K in BankFormMainFieldName]?: string;
};

const transition = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const Form: FC<{
  bank: Bank;
  successQP: { numberOfConnectors: number } & QP;
  afterSubmit: () => void;
  connectors: Connector[];
}> = ({ bank, successQP, afterSubmit, connectors }) => {
  const { t } = useTranslation();

  const userEmail = useGetCurrentCustomerData().data?.email as string;

  const createApi = useCreateBankConnection(successQP);

  const formData = useMemo(() => {
    const data = getFormData({
      fields: bank?.fields,
      email: userEmail,
      financialInstitutionSlug: bank?.financialInstitutionSlug,
    });
    if (bank.status === BankStatus.dev) {
      return { ...data, validationSchema: undefined };
    }
    return data;
  }, [bank, userEmail]);

  const formApi = useForm<BankFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      bankId: bank.id,
      provider: bank.provider,
      ...formData.defaultValues,
    },
    resolver: formData.validationSchema
      ? joiResolver(formData.validationSchema, {
          allowUnknown: true,
          abortEarly: false,
        })
      : undefined,
  });

  const { isSubmitting } = formApi.formState;

  const accountReferenceValue =
    formApi.watch(formData.field.name as BankFormMainFieldName) || '';

  const accountCurrencyValue = formApi.watch('accountCurrency') || '';

  const hasMatchingConnector = checkMatchingConnector(
    connectors,
    accountReferenceValue,
    accountCurrencyValue,
  );

  return (
    <>
      <NewBankIcon className="h-[48px]" />
      <h3 className="text-primary my-2 text-2xl font-semibold">
        {t('banks.bankPicker.step2.title')}
      </h3>
      <div className="text-center text-primary-600">
        <p className="font-semibold ">
          {formData.field.bankFieldType === BankFieldType.iban
            ? t('banks.bankPicker.methods.iban.title')
            : t('banks.bankPicker.methods.email.title')}
        </p>
        <p>{t('banks.bankPicker.methods.subTitle')}</p>
      </div>
      <FormProvider {...formApi}>
        <form
          className="w-full my-8"
          onSubmit={formApi.handleSubmit((values) =>
            createApi.mutateAsync(values).then(afterSubmit),
          )}
        >
          <FieldConnector
            name="customerReference"
            className="mb-3"
            showField={bank?.financialInstitutionSlug === 'deutsche-bank'}
          >
            {({ field }) => (
              <FreeTextInput
                {...field}
                autoFocus
                Icon={UserIcon}
                placeholder={t(
                  'banks.bankPicker.fields.deutsche_username.label',
                )}
                type={formData.field.type}
                tracing={{
                  onFocus: WEBAPP_BANKSLIST_IBANITYMODAL_CLICKEDUSERNAMEINPUT,
                }}
              />
            )}
          </FieldConnector>

          <FieldConnector name={formData.field.name}>
            {({ field: { onChange, ...field } }) => (
              <FreeTextInput
                {...field}
                autoFocus
                Icon={
                  formData.field.bankFieldType === BankFieldType.iban
                    ? BankIcon
                    : UserIconOutline
                }
                placeholder={
                  formData.field.bankFieldType === BankFieldType.iban
                    ? t('banks.bankPicker.fields.iban.label')
                    : t('banks.bankPicker.fields.email.label')
                }
                type={formData.field.type}
                onChange={({ target: { value } }) => {
                  const _value = value || '';
                  onChange(_value);
                }}
                tracing={{
                  onFocus: WEBAPP_BANKSLIST_IBANITYMODAL_CLICKEDIBANINPUT,
                }}
              />
            )}
          </FieldConnector>

          <FieldConnector
            name="accountCurrency"
            label={t('invoice.field.currency')}
            className="mt-3"
          >
            {({ field: { onChange, ...rest } }) => {
              const currencies = Object.keys(CurrenciesEnum).map(
                (currencyCode) => ({
                  label: removeSoftHyphens(t(`currency.${currencyCode}`)),
                  value: currencyCode,
                }),
              );

              return (
                <Select
                  isSearchable
                  placeholder={t('invoice.field.select')}
                  options={currencies}
                  onChange={(option) => {
                    onChange(option.value);
                  }}
                  {...rest}
                />
              );
            }}
          </FieldConnector>

          {hasMatchingConnector ? (
            <div className="mt-4">
              <SimpleAlertV2
                type="warning"
                subtitle={t('banks.bankPicker.has_matching_connector.content')}
              />
            </div>
          ) : null}

          <div className="text-center">
            <Button
              type="submit"
              loading={isSubmitting}
              className="mt-8"
              tracingEvent={WEBAPP_BANKSLIST_IBANITYMODAL_CLICKEDSUBMIT}
            >
              {t('continue')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

const Content: FC<IbanityBankSelectorProps & { afterSubmit: () => void }> = (
  props,
) => {
  const { t } = useTranslation();

  const [selectedBank, setSelectedBank] = useState(props.bank);

  const [mainBanksSearchValue, setMainBanksSearchValue] = useState('');

  const { banksListQuery } = useBanksList();

  const { data, isLoading, isError, refetch } = banksListQuery;

  const banks = data || [];

  const { data: connectorsData } = useGetConnectors();

  const connectors = connectorsData as Connector[];
  const hasConnectorsData = Array.isArray(connectors) && !isEmpty(connectors);

  const banksWithSearch = banks.filter((bank) =>
    bank.name
      ?.toLocaleLowerCase()
      ?.includes(mainBanksSearchValue.toLowerCase()),
  );
  const canAccessAccountableBanking = useCanAccessAccountableBanking();
  const [vividBlockedModal, vividBlockedModalAPI] = useVividBlockedModal(
    canAccessAccountableBanking,
    hasConnectorsData,
    { onOpenBankingSignup: props.onOpenSignup },
  );

  const { data: user } = useGetCurrentCustomerData();
  const isLessProminentProfession = isUserWithLessProminentProfession(user);
  const hasAccountableBanking = useHasAccountableBanking();

  const shouldShowAccountableBankingCard =
    !hasAccountableBanking &&
    canAccessAccountableBanking &&
    isLessProminentProfession;

  return (
    <>
      <div className="pl-2">
        <AnimatePresence mode="wait">
          {!selectedBank &&
            (() => {
              if (isLoading) {
                return (
                  <motion.div
                    key="loading"
                    {...transition}
                    className="min-h-[400px] flex justify-center items-center"
                  >
                    <Loader size="3xl" />
                  </motion.div>
                );
              }
              if (isError) {
                return (
                  <motion.div
                    key="error"
                    {...transition}
                    className="min-h-[400px] flex justify-center items-center"
                  >
                    <SimpleError
                      messageClassName="!text-xl"
                      onRetry={() => refetch()}
                    />
                  </motion.div>
                );
              }
              return (
                <motion.div key="banks_list" {...transition}>
                  <div className="flex flex-col justify-center items-center mb-4 gap-2">
                    <NewBankIcon className="h-[48px]" />
                    <h3 className="text-primary my-2 text-2xl font-semibold">
                      {t('banks.bankPicker.step1.title')}
                    </h3>
                  </div>
                  <SearchBar
                    className="mb-6 pr-4"
                    searchValue={mainBanksSearchValue}
                    setSearchValue={setMainBanksSearchValue}
                    placeholder={t('banks.bankPicker.search_banks_placeholder')}
                  />
                  <motion.div
                    key="data-1"
                    {...transition}
                    className="max-h-[60vh] pr-2 overflow-y-auto force-scroll-style"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      {banksWithSearch.map((bank) => {
                        const isVividBank = bank.id === 'vivid-hardcoded';

                        return (
                          <Button
                            key={bank.id}
                            asDefault
                            onClick={() => {
                              tracingEvents.onSelectBank(bank.name);
                              if (isVividBank) {
                                return vividBlockedModalAPI.open();
                              }
                              setSelectedBank(bank);
                            }}
                            className={classNames(
                              'flex flex-col items-center border border-primary-100 rounded-lg w-full text-sm font-semibold text-primary-900 py-5',
                              'hover:bg-primary-25 focus:bg-primary-25',
                            )}
                          >
                            {bank.logoUrl ? (
                              <img
                                src={bank.logoUrl}
                                alt={bank.name}
                                className="h-12 object-contain"
                              />
                            ) : null}
                            <span className="text-xs text-primary-600">
                              {bank.name}
                            </span>
                          </Button>
                        );
                      })}
                      {shouldShowAccountableBankingCard ? (
                        <Button
                          asDefault
                          onClick={() => {
                            props.onOpenSignup();
                          }}
                          className={classNames(
                            'flex flex-col items-center border border-primary-100 rounded-lg w-full text-sm font-semibold text-primary-900 py-5',
                            'hover:bg-primary-25 focus:bg-primary-25',
                          )}
                        >
                          <img
                            src={accountableBankingLogo}
                            alt="Accountable Banking onboarding"
                            className="h-12 object-contain"
                          />
                          <span className="text-xs text-primary-600">
                            Accountable Banking
                          </span>
                        </Button>
                      ) : null}
                    </div>
                  </motion.div>
                </motion.div>
              );
            })()}
          {!!selectedBank && (
            <motion.div key="bank_form" {...transition}>
              <div className="w-full flex">
                <Button
                  structure="text"
                  onClick={() => setSelectedBank(undefined)}
                >
                  <ArrowLeftIconOutline className="text-light w-6 h-6" />
                </Button>
              </div>
              <motion.div
                key="data-2"
                {...transition}
                className="flex flex-col items-center justify-center px-2"
              >
                <Form
                  bank={selectedBank}
                  successQP={props.successQP || { numberOfConnectors: 0 }}
                  afterSubmit={props.afterSubmit}
                  connectors={connectors}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          className="flex mt-4 justify-center items-center gap-2"
          key="dots"
          {...transition}
        >
          <div
            className={classNames('w-2 h-2 bg-bank-100 rounded-full', {
              'bg-bank': !selectedBank,
            })}
          ></div>
          <div
            className={classNames('w-2 h-2 bg-bank-100 rounded-full', {
              'bg-bank': !!selectedBank,
            })}
          ></div>
        </motion.div>
      </div>
      {vividBlockedModal}
    </>
  );
};

const IbanityBankSelector: FC<
  IbanityBankSelectorProps & { modalRef?: React.RefObject<ModalApi | null> }
> = (props) => {
  return (
    <Modal
      name="Ibanity banks selector"
      className=""
      trigger={props.renderTrigger || (() => null)}
      apiRef={props.modalRef}
      render={(api) => <Content {...props} afterSubmit={api.closeModal} />}
    />
  );
};

export default IbanityBankSelector;
